import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import { SidebarTrigger, useSidebar } from "../ui/sidebar";

/**
 * Props for the AdminHeader component
 */
type AdminHeaderProps = {
  /** Header configuration object with icon and title */
  header: (typeof ADMIN_PANEL_HEADER)[keyof typeof ADMIN_PANEL_HEADER];
};

/**
 * AdminHeader component displays the header for admin pages with an icon, title,
 * and sidebar trigger. Shows sidebar trigger when sidebar is collapsed or on mobile.
 * 
 * @component
 * @param {AdminHeaderProps} props - Component props
 * @param {(typeof ADMIN_PANEL_HEADER)[keyof typeof ADMIN_PANEL_HEADER]} props.header - Header config with icon and title
 * @returns {JSX.Element} Admin header with icon, title, and sidebar trigger
 * 
 * @example
 * ```tsx
 * <AdminHeader header={ADMIN_PANEL_HEADER.USERS} />
 * ```
 */
export default function AdminHeader({ header }: AdminHeaderProps) {
  const { icon: Icon, title } = header;
  const { state: sidebarState, isMobile } = useSidebar();

  return (
    <>
      <div className="flex items-center py-2">
        {(sidebarState === "collapsed" || isMobile) && (
          <div className="mr-3 border-r-[2px] text-primary-500 pr-2">
            <SidebarTrigger />
          </div>
          )}
        <Icon />
        <div className="text-4xl font-medium text-text-500 px-3">{title}</div>
      </div>
    </>
  );
}
